/**
 * AI Interior Designer (/ai-interior-designer): modes, limits and credit
 * prices, shared by the tool UI and its API routes (/api/room-gpt,
 * /api/edit-image) so the Generate button always shows what the server bills.
 *
 * Before 2026-10-10 the button said "4 credits" while edits ran on Nano Banana
 * Pro, which bills 7 at 1K/2K and 12 at 4K since the 2026-09-09 repricing
 * (1,916 of 2,544 interior images in the 30 days before were billed 7).
 *
 * Pure module (imports only other pure constant modules): safe for client
 * bundles.
 */
import {
  DEFAULT_GPT_IMAGE_MODEL,
  getGptImage2FamilyCredits,
} from '@/constants/gptImageModels';
import {
  NANO_BANANA_CREDITS,
  nanoBanana2Credits,
  nanoBananaProCredits,
} from '@/constants/nanoBananaCredits';

export type InteriorMode =
  | 'edit'
  | 'restyle'
  | 'generate'
  | 'generate-360'
  | 'declutter'
  | 'virtual-staging'
  | 'empty-room'
  | 'color-changer';

export interface InteriorModeConfig {
  id: InteriorMode;
  /** English label (also the i18n key in the `ai-interior` namespace). */
  label: string;
  /** One-line helper shown under the mode picker. */
  description: string;
  /** Needs a room photo before it can run. */
  needsPhoto: boolean;
  /** Accepts extra reference photos (furniture, materials, other angles). */
  allowsReferences: boolean;
  /** Uses the room type + style pickers. */
  usesStyles: boolean;
  /** Offers the 1K / 2K / 4K choice. */
  hasResolution: boolean;
  /** Offers the aspect-ratio choice. */
  hasAspectRatio: boolean;
  /** Can produce more than one image per run. */
  allowsCount: boolean;
}

/** Order = order in the mode picker (the three core modes first). */
export const INTERIOR_MODES: InteriorModeConfig[] = [
  {
    id: 'edit',
    label: 'Edit',
    description: 'Change colors, materials or specific items',
    needsPhoto: true,
    allowsReferences: true,
    usesStyles: false,
    hasResolution: true,
    hasAspectRatio: true,
    allowsCount: true,
  },
  {
    id: 'restyle',
    label: 'Restyle',
    description: 'Redesign your room in a new style',
    needsPhoto: true,
    allowsReferences: false,
    usesStyles: true,
    hasResolution: true,
    hasAspectRatio: true,
    allowsCount: true,
  },
  {
    id: 'generate',
    label: 'Create new',
    description: 'Design a room from scratch, no photo needed',
    needsPhoto: false,
    allowsReferences: false,
    usesStyles: true,
    hasResolution: false,
    hasAspectRatio: true,
    allowsCount: true,
  },
  {
    id: 'virtual-staging',
    label: 'Virtual staging',
    description: 'Furnish an empty room',
    needsPhoto: true,
    allowsReferences: false,
    usesStyles: false,
    hasResolution: true,
    hasAspectRatio: false,
    allowsCount: true,
  },
  {
    id: 'declutter',
    label: 'Declutter',
    description: 'Clear away mess and clutter',
    needsPhoto: true,
    allowsReferences: false,
    usesStyles: false,
    hasResolution: true,
    hasAspectRatio: false,
    allowsCount: true,
  },
  {
    id: 'empty-room',
    label: 'Empty room',
    description: 'Remove all furniture and decor',
    needsPhoto: true,
    allowsReferences: false,
    usesStyles: false,
    hasResolution: true,
    hasAspectRatio: false,
    allowsCount: true,
  },
  {
    id: 'color-changer',
    label: 'Paint walls',
    description: 'Try a new wall color, keep everything else',
    needsPhoto: true,
    allowsReferences: false,
    usesStyles: false,
    hasResolution: true,
    hasAspectRatio: false,
    allowsCount: true,
  },
  {
    id: 'generate-360',
    label: '360° view',
    description: 'A panorama you can look around in',
    needsPhoto: false,
    allowsReferences: true,
    usesStyles: true,
    hasResolution: false,
    hasAspectRatio: false,
    allowsCount: false,
  },
];

export const getInteriorMode = (mode: string | null | undefined) =>
  INTERIOR_MODES.find((m) => m.id === mode) || INTERIOR_MODES[0];

export const isInteriorMode = (value: unknown): value is InteriorMode =>
  typeof value === 'string' && INTERIOR_MODES.some((m) => m.id === value);

/** Modes that take one photo and return the same room transformed. */
export const SIMPLE_TRANSFORM_MODES: ReadonlySet<InteriorMode> = new Set([
  'declutter',
  'virtual-staging',
  'empty-room',
  'color-changer',
]);

// Limits ---------------------------------------------------------------------

/** Images per run picked with the count stepper (per style in Restyle/Create). */
export const INTERIOR_MAX_COUNT = 4;
/** Styles compared in one Restyle / Create run. */
export const INTERIOR_MAX_STYLES = 4;
export const INTERIOR_FREE_MAX_STYLES = 1;
/** Hard cap on images one click can start (styles × count). */
export const INTERIOR_MAX_IMAGES_PER_RUN = 8;
/** Room photo + reference photos (Edit and 360° view). */
export const INTERIOR_MAX_PHOTOS = 6;

export const INTERIOR_RESOLUTIONS = ['1K', '2K', '4K'] as const;
export type InteriorResolution = (typeof INTERIOR_RESOLUTIONS)[number];
/** Nano Banana Pro bills 1K and 2K the same, so 2K is the better default. */
export const INTERIOR_DEFAULT_RESOLUTION: InteriorResolution = '2K';
/** Paid-plan only (server-enforced in /api/room-gpt and /api/edit-image). */
export const INTERIOR_PAID_RESOLUTIONS: ReadonlySet<string> = new Set(['4K']);

export const INTERIOR_ASPECT_RATIOS = [
  'auto',
  '1:1',
  '16:9',
  '4:3',
  '3:4',
  '9:16',
];
export const INTERIOR_GENERATE_ASPECT_RATIOS = [
  '16:9',
  '4:3',
  '1:1',
  '3:4',
  '9:16',
];

export const clampInteriorCount = (value: unknown): number => {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, INTERIOR_MAX_COUNT);
};

export const normalizeInteriorResolution = (
  value: unknown,
): InteriorResolution => {
  const v = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return (INTERIOR_RESOLUTIONS as readonly string[]).includes(v)
    ? (v as InteriorResolution)
    : INTERIOR_DEFAULT_RESOLUTION;
};

// Models ---------------------------------------------------------------------

/** Edits, restyles and the one-photo transforms all run on Nano Banana Pro. */
export const INTERIOR_EDIT_MODEL_LABEL = 'Nano Banana Pro';

/** 360° view with reference photos: OpenAI edit at high quality. */
export const INTERIOR_360_EDIT_MODEL = 'OpenAI GPT Image 2.5 Flare High';
/** 360° view from text only. */
export const INTERIOR_360_GENERATE_MODEL = DEFAULT_GPT_IMAGE_MODEL;

export interface InteriorGenerateModel {
  /** Value sent to /api/room-gpt as `model`. */
  value: string;
  label: string;
  credits: number;
}

/**
 * Every model /api/room-gpt accepts for "Create new" (tabs opened before
 * 2026-10-10 may still send any of them). Credits match what the route bills.
 * The page itself offers three of them as quality tiers and never shows the
 * model names.
 */
export const INTERIOR_GENERATE_MODELS: InteriorGenerateModel[] = [
  {
    value: 'Nano Banana 2',
    label: 'Nano Banana 2',
    credits: nanoBanana2Credits('1K'),
  },
  {
    value: 'Nano Banana Pro',
    label: 'Nano Banana Pro',
    credits: nanoBananaProCredits('2K'),
  },
  { value: 'Seedream 5.0 Lite', label: 'Seedream 5.0 Lite', credits: 2 },
  { value: 'Seedream 4.5', label: 'Seedream 4.5', credits: 2 },
  { value: 'FLUX.2 [flex]', label: 'FLUX.2 [flex]', credits: 3 },
  { value: 'Nano Banana', label: 'Nano Banana', credits: NANO_BANANA_CREDITS },
  { value: 'Z-Image Turbo', label: 'Z-Image Turbo', credits: 1 },
];

export const INTERIOR_DEFAULT_GENERATE_MODEL = 'Nano Banana 2';

export const getInteriorGenerateModel = (value: string | null | undefined) =>
  INTERIOR_GENERATE_MODELS.find((m) => m.value === value) ||
  INTERIOR_GENERATE_MODELS[0];

export type InteriorQuality = 'fast' | 'standard' | 'best';

export interface InteriorQualityTier {
  id: InteriorQuality;
  /** English label (also the i18n key). */
  label: string;
  /** One-line hint under the picker (i18n key). */
  hint: string;
  /** Model behind the tier. Never shown on the page. */
  model: string;
}

/** "Create new" quality choices, cheapest first. */
export const INTERIOR_QUALITY_TIERS: InteriorQualityTier[] = [
  {
    id: 'fast',
    label: 'Fast',
    hint: 'Ready in seconds, good for trying ideas',
    model: 'Z-Image Turbo',
  },
  {
    id: 'standard',
    label: 'Standard',
    hint: 'Sharp, realistic designs for most rooms',
    model: INTERIOR_DEFAULT_GENERATE_MODEL,
  },
  {
    id: 'best',
    label: 'Best quality',
    hint: 'The finest detail and the most faithful styles',
    model: 'Nano Banana Pro',
  },
];

export const INTERIOR_DEFAULT_QUALITY: InteriorQuality = 'standard';

export const isInteriorQuality = (value: unknown): value is InteriorQuality =>
  typeof value === 'string' &&
  INTERIOR_QUALITY_TIERS.some((tier) => tier.id === value);

export const getInteriorQualityTier = (value: unknown): InteriorQualityTier =>
  INTERIOR_QUALITY_TIERS.find((tier) => tier.id === value) ||
  (INTERIOR_QUALITY_TIERS.find(
    (tier) => tier.id === INTERIOR_DEFAULT_QUALITY,
  ) as InteriorQualityTier);

/**
 * Model of a "Create new" request: the page sends `quality`, older tabs and
 * scripts send `model`.
 */
export const resolveInteriorGenerateModel = ({
  quality,
  model,
}: {
  quality?: unknown;
  model?: unknown;
}): InteriorGenerateModel =>
  getInteriorGenerateModel(
    isInteriorQuality(quality)
      ? getInteriorQualityTier(quality).model
      : typeof model === 'string'
        ? model
        : null,
  );

// Credits --------------------------------------------------------------------

export interface InteriorRunInput {
  mode: InteriorMode;
  /** Images per style (Restyle / Create) or per run (other modes). */
  count: number;
  /** Styles picked (Restyle / Create); ignored elsewhere. */
  styleCount?: number;
  resolution?: string;
  /** "Create new" quality tier (wins over `model`). */
  quality?: string;
  /** "Create new" model value, from requests made before quality tiers. */
  model?: string;
  /** Room photo + reference photos sent (360° view picks its model by this). */
  photoCount?: number;
}

export interface InteriorRunPlan {
  images: number;
  creditsPerImage: number;
  totalCredits: number;
}

export const getInteriorCreditsPerImage = ({
  mode,
  resolution,
  quality,
  model,
  photoCount = 0,
}: Omit<InteriorRunInput, 'count' | 'styleCount'>): number => {
  if (mode === 'generate') {
    return resolveInteriorGenerateModel({ quality, model }).credits;
  }
  if (mode === 'generate-360') {
    return photoCount > 0
      ? (getGptImage2FamilyCredits(INTERIOR_360_EDIT_MODEL) ?? 11)
      : (getGptImage2FamilyCredits(INTERIOR_360_GENERATE_MODEL) ?? 3);
  }
  return nanoBananaProCredits(normalizeInteriorResolution(resolution));
};

export const planInteriorRun = (input: InteriorRunInput): InteriorRunPlan => {
  const config = getInteriorMode(input.mode);
  const count = config.allowsCount ? clampInteriorCount(input.count) : 1;
  const styles =
    config.usesStyles && config.allowsCount
      ? Math.max(1, Math.floor(Number(input.styleCount) || 1))
      : 1;
  const images = count * styles;
  const creditsPerImage = getInteriorCreditsPerImage(input);
  return {
    images,
    creditsPerImage,
    totalCredits: Math.round(creditsPerImage * images * 100) / 100,
  };
};

export type InteriorAllowanceResult = 'ok' | 'first-free' | 'insufficient';

/**
 * Whether a run fits the account's image credits. A run must fit what is left
 * (`allowance - used`), with one exception kept from the old route: a Free
 * account that has used nothing this period may try one single-image run,
 * even when it costs more than the 3-credit Free allowance.
 */
export const checkInteriorAllowance = ({
  used,
  allowance,
  totalCredits,
  images,
  isFreePlan,
}: {
  used: number;
  allowance: number;
  totalCredits: number;
  images: number;
  isFreePlan: boolean;
}): InteriorAllowanceResult => {
  const spent = Math.max(0, Number(used) || 0);
  const remaining = Math.max(0, (Number(allowance) || 0) - spent);
  if (totalCredits <= remaining) return 'ok';
  if (isFreePlan && spent === 0 && images === 1) return 'first-free';
  return 'insufficient';
};

// Content --------------------------------------------------------------------

/**
 * Room type values sent to the API (English); labels go through t(). Grouped
 * for the picker. The values are the ones the old dropdown offered.
 */
export const INTERIOR_ROOM_TYPE_GROUPS: { label: string; rooms: string[] }[] = [
  {
    label: 'Home',
    rooms: [
      'Living Room',
      'Bedroom',
      'Kitchen',
      'Bathroom',
      'Dining Room',
      'Home Office',
      'Kids Room',
      'Kitchen Living Combo',
      'Apartment / Studio Apartment',
      'Attic',
      'Study Room',
      'Gaming Room',
      'Walk in Closet',
      'Laundry Room',
      'Mudroom',
      'Drop Zone',
      'Toilet',
      'Garage',
      'Garage Gym',
      'Tiny House / Micro Home',
      'Van Interior / Campervan',
    ],
  },
  {
    label: 'Outdoor',
    rooms: [
      'House Exterior',
      'Front Yard',
      'Outdoor Patio',
      'Outdoor Garden',
      'Outdoor Kitchen',
      'Outdoor Pool Area',
      'Swimming Pool',
      'Deck',
      'Carport',
      'Outdoor Garage',
    ],
  },
  {
    label: 'Business',
    rooms: [
      'Office',
      'Meeting Room',
      'Coworking Space',
      'Podcast Studio',
      'Workshop',
      'Coffee Shop',
      'Restaurant',
      'Bakery',
      'Store',
      'Clothing Store',
      'Barbershop',
      'Tattoo Studio',
      'Dental Office',
      'Pharmacy',
      'Gym',
      'Pilates / Barre Studio',
      'Daycare / Childcare Center',
      'Hotel Lobby',
      'Hotel Room',
      'Hotel Bathroom',
      'Exhibition Space',
      'Onsen',
    ],
  },
  { label: 'Other', rooms: ['Other'] },
];

export const INTERIOR_ROOM_TYPES = INTERIOR_ROOM_TYPE_GROUPS.flatMap(
  (group) => group.rooms,
);

export const INTERIOR_DEFAULT_ROOM_TYPE = 'Living Room';

export interface InteriorSampleRoom {
  slug: string;
  /** Room type picked along with the sample. */
  roomType: string;
  /** English label (i18n key). */
  label: string;
  before: string;
  after: string;
}

const SAMPLE_BASE = 'https://media.easy-peasy.ai/room-types';

const sample = (slug: string, roomType: string, label = roomType) => ({
  slug,
  roomType,
  label,
  before: `${SAMPLE_BASE}/${slug}-before.jpeg`,
  after: `${SAMPLE_BASE}/${slug}-after-1.jpeg`,
});

/**
 * Real before/after pairs from the room landing pages (1200×896). The
 * "before" photos double as sample inputs for visitors without a photo.
 */
export const INTERIOR_SAMPLE_ROOMS: InteriorSampleRoom[] = [
  sample('living-room', 'Living Room'),
  sample('kitchen', 'Kitchen'),
  sample('bedroom', 'Bedroom'),
  sample('bathroom', 'Bathroom'),
  sample('dining-room', 'Dining Room'),
  sample('home-office', 'Home Office'),
];

/** One-click prompts for Edit. English text is sent to the model. */
export const INTERIOR_EDIT_SUGGESTIONS = [
  'Paint the walls sage green',
  'Replace the sofa with a cognac leather sofa',
  'Add large indoor plants',
  'Add warm, cozy lighting',
  'Change the floor to light oak wood',
  'Add a large abstract painting on the wall',
];

/** Swatches for Paint walls. `label` is also the color sent to the model. */
export const INTERIOR_PAINT_COLORS: { label: string; hex: string }[] = [
  { label: 'Warm white', hex: '#F3EEE3' },
  { label: 'Greige', hex: '#C8BEB0' },
  { label: 'Sage green', hex: '#A9B79A' },
  { label: 'Terracotta', hex: '#C46E4F' },
  { label: 'Dusty blue', hex: '#8EA4B8' },
  { label: 'Navy blue', hex: '#26344F' },
  { label: 'Charcoal', hex: '#3F4246' },
  { label: 'Blush pink', hex: '#E5C3BC' },
];

export const INTERIOR_STAGING_SUGGESTIONS = [
  'Modern minimalist furniture',
  'Cozy Scandinavian style',
  'Warm mid-century modern',
  'Luxury hotel style',
];
